import React, { useEffect, useMemo, useRef, useState } from 'react';
import { carteraApi } from './api';
import { MensajeRapidoModal } from './MensajeRapidoModal';
import { money, money0, iniciales, avaColor, PROD, prodCorto } from './helpers';

/*
 * Matriz de cobranza — contratos × meses.
 *
 * Es la hoja que Dirección lleva en Excel: cada renglón un contrato, cada
 * columna un mes, y en la celda lo que FALTA por cobrar de ese mes. De un
 * vistazo se ve quién debe qué meses. A diferencia del Excel, esto lee las
 * mismas cuotas que mueve el registro de pagos: al aplicar un pago la celda se
 * limpia sola.
 *
 * El click en la fila abre el detalle del crédito, que es donde se registran
 * los pagos y se capturan las notas de cobranza.
 *
 * ── Acciones (mensaje rápido) ─────────────────────────────────────────────
 * La columna de acciones y el badge de mensajes sin leer SOLO aparecen en el
 * mes en curso. El motivo no es estético: la matriz ignora por completo el
 * `?hasta` para todo lo que no sean las columnas —el universo de contratos, la
 * mora y el reloj son siempre HOY—, así que navegando a julio se ven columnas
 * de julio con datos de septiembre. Ofrecer ahí un botón que manda un WhatsApp
 * con los montos de hoy sobre una vista que dice julio es la clase de trampa
 * que nadie descubre hasta que un cliente contesta.
 */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const VENTANAS = [3, 6, 12];
const TIPO_SEG = [['todos', 'Todo producto'], ['AP', 'Puro'], ['AF', 'Financiero'], ['CS', 'Crédito simple']];

/*
 * Por qué no se le puede escribir. Las claves las manda el backend
 * (DestinatarioCobranzaService) y son estables; el texto corto va en la celda
 * y el largo en el title.
 */
const MOTIVO_NO_ENVIABLE_CORTO = {
  sin_telefono: 'Sin teléfono',
  telefono_interno: 'Número interno',
  opt_out: 'Pidió baja',
  numero_equivocado: 'Número mal',
  bloqueada: 'Bloqueada',
  conversacion_inactiva: 'Hilo inactivo',
};

const MOTIVO_NO_ENVIABLE = {
  sin_telefono: 'Este contrato no tiene un teléfono válido. Captúralo desde el detalle del crédito.',
  telefono_interno: 'El teléfono registrado es el número interno de avisos al asesor, no el del cliente. Hay que corregirlo.',
  opt_out: 'El cliente pidió BAJA: no se le pueden enviar plantillas.',
  numero_equivocado: 'El número está marcado como equivocado.',
  bloqueada: 'La conversación de WhatsApp está bloqueada.',
  conversacion_inactiva: 'La conversación de WhatsApp no está activa.',
};

const periodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftPeriodo = (periodo, delta) => {
  const [y, m] = periodo.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const periodoLabel = (periodo) => {
  const [y, m] = periodo.split('-').map(Number);
  return `${MESES[m - 1]} ${y}`;
};

/* Monto de la celda: la cuota pendiente, más la mora si el switch está activo. */
const montoCelda = (celda, conMora) => (celda ? celda.pendiente + (conMora ? celda.mora : 0) : 0);

const Skeleton = () => (
  <div className="co-card" style={{ padding: 18 }}>
    <div className="sk" style={{ height: 14, width: 220, marginBottom: 18 }} />
    {[...Array(9)].map((_, i) => (
      <div key={i} className="sk" style={{ height: 34, width: '100%', marginBottom: 8, opacity: 1 - i * 0.07 }} />
    ))}
  </div>
);

/* Una celda mes: vacía cuando no había obligación, ✓ cuando quedó saldada. */
const Celda = ({ celda, conMora }) => {
  if (!celda) return <td className="mtz-cell na">—</td>;

  const monto = montoCelda(celda, conMora);
  if (celda.estado === 'pagado') {
    return (
      <td className="mtz-cell pagado" title={`Pagado · ${money(celda.esperado)}`}>
        <i className="pi pi-check" />
      </td>
    );
  }

  const titulo = [
    `Cuota ${money(celda.esperado)}`,
    celda.pagado > 0 ? `abonado ${money(celda.pagado)}` : null,
    `pendiente ${money(celda.pendiente)}`,
    celda.mora > 0 ? `mora ${money(celda.mora)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <td className={'mtz-cell ' + celda.estado} title={titulo}>
      <b>{money0(monto)}</b>
      {celda.parcial && <span className="mtz-parcial">parcial</span>}
      {conMora && celda.mora > 0 && <span className="mtz-mora">+mora</span>}
    </td>
  );
};

export const CarteraMatriz = ({ onOpenCredito, onGoConversacion }) => {
  const [hasta, setHasta] = useState(periodoActual());
  const [msgCreditoId, setMsgCreditoId] = useState(null);
  const [ventana, setVentana] = useState(3);
  const [conMora, setConMora] = useState(true);
  const [tipo, setTipo] = useState('todos');
  const [search, setSearch] = useState('');
  const [soloAdeudo, setSoloAdeudo] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await carteraApi.getMatriz({ hasta, meses: ventana }, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setData(res?.data || null);
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.message || 'No se pudo cargar la matriz de cobranza.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [hasta, ventana, reload]);

  const columnas = useMemo(() => data?.columnas || [], [data]);

  // Búsqueda y filtros en memoria: la cartera cabe entera en la respuesta y así
  // el filtrado es instantáneo (sin ida al servidor por cada tecla).
  const filas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.filas || []).filter((f) => {
      if (tipo !== 'todos' && prodCorto(f.producto) !== tipo) return false;
      if (soloAdeudo && f.total_pendiente <= 0) return false;
      if (q && !`${f.clave_contrato} ${f.cliente} ${f.vehiculo || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, tipo, search, soloAdeudo]);

  // Si nadie arrastra morosidad fuera de la ventana, la columna sobra.
  const hayAnteriores = useMemo(() => filas.some((f) => f.anteriores.pendiente > 0), [filas]);

  // Ver el encabezado: las acciones solo tienen sentido sobre el mes en curso.
  const esMesActual = hasta === periodoActual();

  /*
   * Mensajes sin leer, colapsados por CLIENTE.
   *
   * `no_leidos` viene por conversación (= por teléfono), y un cliente con tres
   * unidades trae el mismo contador en sus tres filas: pintarlo tal cual haría
   * leer 9 mensajes donde hay 3. Se colapsa a la PRIMERA fila visible de cada
   * cliente, y hay que agrupar explícitamente porque la matriz ordena por día
   * de pago: las filas de un mismo cliente no quedan contiguas.
   */
  const badgeEn = useMemo(() => {
    const vistos = new Set();
    const marcadas = new Set();
    filas.forEach((f) => {
      if (!f.no_leidos || !f.cliente_key || vistos.has(f.cliente_key)) return;
      vistos.add(f.cliente_key);
      marcadas.add(f.credito_id);
    });
    return marcadas;
  }, [filas]);

  // Pie calculado sobre lo VISIBLE: con un filtro puesto, los totales del
  // servidor mentirían respecto de lo que el usuario tiene enfrente.
  const pie = useMemo(() => {
    const porMes = {};
    columnas.forEach((c) => { porMes[c.periodo] = { esperado: 0, cobrado: 0, pendiente: 0, mora: 0 }; });
    let anteriores = 0;
    let totalPendiente = 0;
    let mensualidad = 0;

    filas.forEach((f) => {
      mensualidad += f.mensualidad;
      anteriores += f.anteriores.pendiente + (conMora ? f.anteriores.mora : 0);
      totalPendiente += f.total_pendiente + (conMora ? f.total_mora : 0);
      columnas.forEach((c) => {
        const celda = f.celdas[c.periodo];
        if (!celda) return;
        const acc = porMes[c.periodo];
        acc.esperado += celda.esperado;
        acc.cobrado += celda.pagado;
        acc.pendiente += celda.pendiente;
        acc.mora += celda.mora;
      });
    });

    return { porMes, anteriores, totalPendiente, mensualidad };
  }, [filas, columnas, conMora]);

  const exportar = () => {
    const cols = [
      'Contrato', 'Cliente', 'Vehículo', 'Producto', 'Día de pago', 'Mensualidad',
      ...(hayAnteriores ? ['Anteriores'] : []),
      ...columnas.map((c) => periodoLabel(c.periodo)),
      'Total por cobrar', 'Mora', 'Comentario',
    ];
    const num = (n) => (n ? Number(n).toFixed(2) : '');
    const filasCsv = filas.map((f) => [
      f.clave_contrato, f.cliente, f.vehiculo || '', f.producto, f.dia_de_pago, num(f.mensualidad),
      ...(hayAnteriores ? [num(f.anteriores.pendiente + (conMora ? f.anteriores.mora : 0))] : []),
      ...columnas.map((c) => {
        const celda = f.celdas[c.periodo];
        if (!celda) return '';
        return celda.estado === 'pagado' ? '' : num(montoCelda(celda, conMora));
      }),
      num(f.total_pendiente + (conMora ? f.total_mora : 0)), num(f.total_mora), f.comentario?.texto || '',
    ]);

    const morosidad = [
      'Por cobrar', '', '', '', '', num(pie.mensualidad),
      ...(hayAnteriores ? [num(pie.anteriores)] : []),
      ...columnas.map((c) => num(pie.porMes[c.periodo].pendiente + (conMora ? pie.porMes[c.periodo].mora : 0))),
      num(pie.totalPendiente), '', '',
    ];
    const efectividad = [
      '% cobrado', '', '', '', '', '',
      ...(hayAnteriores ? [''] : []),
      ...columnas.map((c) => {
        const m = pie.porMes[c.periodo];
        return m.esperado > 0 ? (m.cobrado / m.esperado * 100).toFixed(1) + '%' : '';
      }),
      '', '', '',
    ];

    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols, ...filasCsv, [], morosidad, efectividad].map((r) => r.map(esc).join(',')).join('\r\n');
    // BOM: sin él Excel abre los acentos como mojibake.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `matriz-cobranza-${hasta}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const totales = data?.totales;

  return (
    <div className="co-fade">
      <div className="page-head">
        <div>
          <div className="eyebrow">Operación</div>
          <h2>Matriz de cobranza</h2>
          <p>
            {loading || !totales
              ? 'Cargando contratos…'
              : `${filas.length} contratos · ${money0(pie.mensualidad)} de mensualidad · ${money0(pie.totalPendiente)} por cobrar`}
          </p>
        </div>
        <div className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => setReload((n) => n + 1)} disabled={loading} title="Volver a leer la cartera">
          <i className="pi pi-refresh" />Actualizar
        </button>
        <button className="btn btn-ghost btn-sm" onClick={exportar} disabled={loading || !filas.length}>
          <i className="pi pi-download" />Exportar
        </button>
      </div>

      <div className="filters">
        <div className="mtz-periodo">
          <button className="btn btn-sm btn-ghost" onClick={() => setHasta((p) => shiftPeriodo(p, -1))} title="Mes anterior">
            <span className="pi pi-chevron-left" />
          </button>
          <span className="tnum">{periodoLabel(hasta)}</span>
          <button className="btn btn-sm btn-ghost" onClick={() => setHasta((p) => shiftPeriodo(p, 1))} title="Mes siguiente">
            <span className="pi pi-chevron-right" />
          </button>
        </div>
        {hasta !== periodoActual() && (
          <button className="btn btn-sm btn-cyan" onClick={() => setHasta(periodoActual())}>Volver a hoy</button>
        )}

        <div className="seg">
          {VENTANAS.map((v) => (
            <button key={v} className={ventana === v ? 'on' : ''} onClick={() => setVentana(v)}>{v} meses atrás</button>
          ))}
        </div>

        <label className="mtz-switch" title="Suma los intereses moratorios devengados a cada mes vencido">
          <input type="checkbox" checked={conMora} onChange={(e) => setConMora(e.target.checked)} />
          <span>Incluir moratorios</span>
        </label>

        <div className="spacer" style={{ flex: 1 }} />

        <label className="mtz-switch">
          <input type="checkbox" checked={soloAdeudo} onChange={(e) => setSoloAdeudo(e.target.checked)} />
          <span>Solo con adeudo</span>
        </label>

        <div className="seg">
          {TIPO_SEG.map(([v, l]) => (
            <button key={v} className={tipo === v ? 'on' : ''} onClick={() => setTipo(v)}>{l}</button>
          ))}
        </div>

        <div className="mtz-search">
          <i className="pi pi-search" />
          <input placeholder="Buscar contrato o cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {loading && <Skeleton />}

      {!loading && error && (
        <div className="co-card empty-state">
          <i className="pi pi-exclamation-triangle" />
          <div className="es-title">{error}</div>
          <button className="btn btn-sm btn-ghost" onClick={() => setReload((n) => n + 1)}>Reintentar</button>
        </div>
      )}

      {!loading && !error && !filas.length && (
        <div className="co-card empty-state">
          <i className="pi pi-inbox" />
          <div className="es-title">Sin contratos que mostrar</div>
          <div className="es-sub">Ajusta los filtros o cambia el mes de la vista.</div>
        </div>
      )}

      {!loading && !error && !!filas.length && (
        <>
          <div className="co-card mtz-wrap">
            <table className="mtz">
              <thead>
                <tr>
                  <th className="stick">Contrato</th>
                  <th className="num">Día</th>
                  <th className="num">Mensualidad</th>
                  {hayAnteriores && <th className="num">Anteriores</th>}
                  {columnas.map((c) => (
                    <th key={c.periodo} className={'num mes ' + c.tipo}>
                      {periodoLabel(c.periodo)}
                      <em>{c.tipo === 'vencido' ? 'vencido' : c.tipo === 'actual' ? 'en curso' : 'próximo'}</em>
                    </th>
                  ))}
                  <th className="num">Por cobrar</th>
                  <th className="mtz-com">Comentario</th>
                  {esMesActual && <th className="mtz-acc">Acciones</th>}
                </tr>
              </thead>

              <tbody>
                {filas.map((f) => {
                  const prod = PROD[prodCorto(f.producto)];
                  const totalFila = f.total_pendiente + (conMora ? f.total_mora : 0);
                  return (
                    <tr key={f.credito_id} onClick={() => onOpenCredito?.(f.credito_id)} title="Ver detalle del contrato">
                      <td className="stick">
                        <div className="mtz-cli">
                          <div className="ava" style={{ background: avaColor(f.cliente) }}>{iniciales(f.cliente)}</div>
                          <div className="txt">
                            <b>
                              {f.clave_contrato}
                              <span className="tag" style={{ background: prod.bg, color: prod.color }}>{prodCorto(f.producto)}</span>
                              {esMesActual && badgeEn.has(f.credito_id) && (
                                <button
                                  type="button"
                                  className="mtz-nuevos tnum"
                                  title={`${f.no_leidos} mensaje${f.no_leidos === 1 ? '' : 's'} sin leer · abrir la conversación`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // La key del hilo la define el backend: el
                                    // id de la conversación, o 'nuevo-{credito}'
                                    // cuando el contrato aún no tiene hilo.
                                    onGoConversacion?.(f.conversacion_id ? String(f.conversacion_id) : `nuevo-${f.credito_id}`);
                                  }}
                                >
                                  <i className="pi pi-comment" />{f.no_leidos}
                                </button>
                              )}
                            </b>
                            <span>{f.cliente}{f.vehiculo ? ` · ${f.vehiculo}` : ''}</span>
                          </div>
                        </div>
                      </td>
                      <td className="num dim">{f.dia_de_pago}</td>
                      <td className="num">{money0(f.mensualidad)}</td>
                      {hayAnteriores && (
                        <td className="mtz-cell ant">
                          {f.anteriores.pendiente > 0 ? (
                            <>
                              <b>{money0(f.anteriores.pendiente + (conMora ? f.anteriores.mora : 0))}</b>
                              <span className="mtz-mas">+{f.anteriores.meses} {f.anteriores.meses === 1 ? 'mes' : 'meses'}</span>
                            </>
                          ) : <span className="na">—</span>}
                        </td>
                      )}
                      {columnas.map((c) => (
                        <Celda key={c.periodo} celda={f.celdas[c.periodo]} conMora={conMora} />
                      ))}
                      <td className={'num total' + (totalFila > 0 ? ' deuda' : '')}>{totalFila > 0 ? money0(totalFila) : '—'}</td>
                      <td className="mtz-com">
                        {f.comentario ? (
                          <span className={'mtz-nota ' + f.comentario.tipo} title={f.comentario.autor ? `${f.comentario.texto} — ${f.comentario.autor}` : f.comentario.texto}>
                            {f.comentario.tipo === 'promesa_pago' && <i className="pi pi-flag-fill" />}
                            {f.comentario.tipo === 'pago_reportado' && <i className="pi pi-exclamation-circle" />}
                            {f.comentario.texto}
                          </span>
                        ) : <span className="na">—</span>}
                      </td>
                      {esMesActual && (
                        // stopPropagation en la CELDA, no solo en el botón: el
                        // onClick que abre el detalle vive en el <tr> y el CSS
                        // le pone cursor:pointer a toda la fila.
                        <td className="mtz-acc" onClick={(e) => e.stopPropagation()}>
                          {f.puede_enviar ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-cyan"
                              onClick={() => setMsgCreditoId(f.credito_id)}
                            >
                              <i className="pi pi-whatsapp" />Enviar mensaje
                            </button>
                          ) : (
                            <span className="na" title={MOTIVO_NO_ENVIABLE[f.motivo_no_enviable] || 'No se le puede escribir.'}>
                              {MOTIVO_NO_ENVIABLE_CORTO[f.motivo_no_enviable] || '—'}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td className="stick">Por cobrar</td>
                  <td />
                  <td className="num">{money0(pie.mensualidad)}</td>
                  {hayAnteriores && <td className="num deuda">{pie.anteriores > 0 ? money0(pie.anteriores) : '—'}</td>}
                  {columnas.map((c) => {
                    const m = pie.porMes[c.periodo];
                    const v = m.pendiente + (conMora ? m.mora : 0);
                    return <td key={c.periodo} className={'num' + (v > 0 ? ' deuda' : '')}>{v > 0 ? money0(v) : '—'}</td>;
                  })}
                  <td className="num deuda">{money0(pie.totalPendiente)}</td>
                  <td />
                  {esMesActual && <td />}
                </tr>
                <tr className="tf-pend">
                  <td className="stick muted">% cobrado</td>
                  <td />
                  <td />
                  {hayAnteriores && <td />}
                  {columnas.map((c) => {
                    const m = pie.porMes[c.periodo];
                    // El mes próximo aún no se cobra: un % ahí solo confunde.
                    if (c.tipo === 'proximo' || m.esperado <= 0) return <td key={c.periodo} className="num muted">—</td>;
                    const pct = m.cobrado / m.esperado * 100;
                    return (
                      <td key={c.periodo} className="num">
                        <span className={'mtz-pct ' + (pct >= 95 ? 'ok' : pct >= 80 ? 'warn' : 'bad')}>{pct.toFixed(1)}%</span>
                      </td>
                    );
                  })}
                  <td />
                  <td />
                  {esMesActual && <td />}
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mtz-leyenda">
            <span><i className="sw pagado" />Pagado</span>
            <span><i className="sw vencido" />Vencido</span>
            <span><i className="sw porvencer" />Del mes en curso</span>
            <span><i className="sw proximo" />Mes próximo</span>
            <span><i className="sw na" />Sin cuota ese mes</span>
            <span className="dim">Los montos son lo que falta por cobrar{conMora ? ', moratorios incluidos' : ''}.</span>
            {!esMesActual && (
              <span className="dim">Las acciones solo están disponibles en el mes en curso.</span>
            )}
          </div>
        </>
      )}

      {msgCreditoId && (
        <MensajeRapidoModal
          creditoId={msgCreditoId}
          onClose={() => setMsgCreditoId(null)}
          // Se recarga la matriz al enviar: el badge de no leídos y el estado
          // de la conversación pueden haber cambiado.
          onEnviado={() => setReload((n) => n + 1)}
        />
      )}
    </div>
  );
};
